import Modal from '@components/common/Modal/Modal';
import {useEffect, useState} from 'react';
import ActionButton from '@components/common/ActionButton/ActionButton';
import {toastWarning} from '@components/common/Toast/CustomToast';
import {Alert} from '@/utils/alert';
import SelectInput from '@components/common/SelectInput/SelectInput';
import TextInput from '@components/common/TextInput/TextInput';
import {api} from '@/api/growth/farmland/api';
import {
    farmingHist,
    FarmlandAddReq,
    SoilTestAddReq,
} from '@/api/growth/farmland/type';
import DatePicker from '@components/common/DatePicker';
import {formatDateString, parseDate} from '@utils/Utils';
import Tabs from '@components/common/Tabs';
import GrowthManagement from '@components/page/Growth/GrowthManagement';

interface FarmlandAddProps {
    gsIdx: number;
    address: string;
    area: number;
    lat: number;
    lon: number;
    jimok: string;
    onClose: () => void;
    onSuccess: () => void;
}

const FarmlandAdd = (param: FarmlandAddProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [formData, setFormData] = useState<FarmlandAddReq>({
        gsIdx: 0,
        gcIdx: 0,
        guIdx: 0,
        farmlandName: '',
        lon: '',
        lat: '',
        address: '',
        area: 0,
        jimok: '',
        growthFlag: 0,
        soilTestList: [],
        farmingHistList: [],
        pdate: '',
        ppop: '-99',
        plme: '-99',
        plds: '-99',
        plrs: '-99',
        pldp: '-99',
        page: '-99',
        penv: '-99',
        plph: '-99',
        sprl: '-99',
    });

    const [soilTests, setSoilTests] = useState<SoilTestAddReq[]>([
        {
            depth: 0,
            ph: 0,
            ec: 0,
            organicMatter: 0,
            toc: 0,
            totalNitrogen: 0,
            ammonia: 0,
            nitricAcid: 0,
            cec: 0,
            exchangeableCa: 0,
            exchangeableMg: 0,
            exchangeableK: 0,
            availablePhosphorus: 0,
            bulkDensity: 0,
            solid: 0,
            liquid: 0,
            air: 0,
            porosity: 0,
            moistureMatter: 0,
            soilTexture: '',
            sand: 0,
            silt: 0,
            clay: 0,
            testDt: '',
        },
    ]);

    const openPop = (): void => {
        setIsModalOpen(true);
    };

    const closePop = (): void => {
        setIsModalOpen(false);
        param.onClose();
    };

    const handleInputChange = (
        field: string,
        value: string | number | farmingHist[],
    ) => {
        setFormData(prev => ({...prev, [field]: value}));
    };

    const handleSoilTestChange = (
        index: number,
        field: keyof SoilTestAddReq,
        value: string | number,
    ) => {
        setSoilTests(prev => {
            const updated = [...prev];
            updated[index] = {...updated[index], [field]: value};
            return updated;
        });
    };

    const addSoilTest = () => {
        if (soilTests.length >= 5) {
            toastWarning('토양분석 데이터는 최대 5개까지 추가할 수 있습니다.');
            return;
        }
        setSoilTests(prev => [
            ...prev,
            {
                depth: 0,
                ph: 0,
                ec: 0,
                organicMatter: 0,
                toc: 0,
                totalNitrogen: 0,
                ammonia: 0,
                nitricAcid: 0,
                cec: 0,
                exchangeableCa: 0,
                exchangeableMg: 0,
                exchangeableK: 0,
                availablePhosphorus: 0,
                bulkDensity: 0,
                solid: 0,
                liquid: 0,
                air: 0,
                porosity: 0,
                moistureMatter: 0,
                soilTexture: '',
                sand: 0,
                silt: 0,
                clay: 0,
                testDt: '',
            },
        ]);
    };

    const removeSoilTest = (index: number) => {
        setSoilTests(prev => prev.filter((_, i) => i !== index));
    };

    const validateForm = async () => {
        const requiredElements = document.querySelectorAll(
            'input[data-required="true"], select[data-required="true"]',
        );
        const invalidFields: string[] = [];

        requiredElements.forEach(element => {
            if (element.getAttribute('data-valid') === 'false') {
                // label에서 필드명 추출 또는 data-label 속성 사용
                const label =
                    element.getAttribute('data-label') ||
                    element.getAttribute('placeholder') ||
                    element.id ||
                    'Unknown Field';

                invalidFields.push(label);
            }
        });

        if (invalidFields.length > 0) {
            const fieldList = invalidFields
                .filter((item, index) => invalidFields.indexOf(item) === index) // 중복 제거
                .join(', ');

            toastWarning(`다음 필수 항목을 확인해주세요: ${fieldList}`);
            return false;
        }

        return true;
    };

    const soilTextureOptions = [
        {value: 'sand', label: '모래(Sand)'},
        {value: 'silt', label: '미사(Silt)'},
        {value: 'clay', label: '점토(Clay)'},
    ];

    const validateFarmingHistList = (): boolean => {
        // 물관리(I) 데이터 검증
        const irrigations = (formData.farmingHistList || []).filter(
            h => h.category === 'I',
        );
        for (let i = 0; i < irrigations.length; i++) {
            const r = irrigations[i];
            if (!r.workDt?.trim()) {
                toastWarning(`물관리 ${i + 1}번째: 작업일자를 선택해주세요.`);
                return false;
            }
            if (!r.iame?.trim()) {
                toastWarning(`물관리 ${i + 1}번째: 관개 방법을 선택해주세요.`);
                return false;
            }
            if (!r.iamt?.toString().trim()) {
                toastWarning(`물관리 ${i + 1}번째: 관개량(mm)을 입력해주세요.`);
                return false;
            }
        }

        // 무기질비료(F) 데이터 검증
        const fertilizers = (formData.farmingHistList || []).filter(
            h => h.category === 'F',
        );

        for (let i = 0; i < fertilizers.length; i++) {
            const r = fertilizers[i];
            if (!r.workDt?.trim()) {
                toastWarning(
                    `무기질비료 ${i + 1}번째: 작업일자를 선택해주세요.`,
                );
                return false;
            }
            if (!r.fmcd?.trim()) {
                toastWarning(
                    `무기질비료 ${i + 1}번째: 비료종류를 선택해주세요.`,
                );
                return false;
            }
            if (!r.facd?.trim()) {
                toastWarning(
                    `무기질비료 ${i + 1}번째: 시비방법을 선택해주세요.`,
                );
                return false;
            }
            if (!r.famn?.toString().trim()) {
                toastWarning(
                    `무기질비료 ${i + 1}번째: 질소(N) 시비량을 입력해주세요.`,
                );
                return false;
            }
            if (!r.famp?.toString().trim()) {
                toastWarning(
                    `무기질비료 ${i + 1}번째: 인산(P₂O₅) 시비량을 입력해주세요.`,
                );
                return false;
            }
            if (!r.famk?.toString().trim()) {
                toastWarning(
                    `무기질비료 ${i + 1}번째: 칼리(K₂O) 시비량을 입력해주세요.`,
                );
                return false;
            }
        }

        // 유기질비료(R) 데이터 검증
        const residues = (formData.farmingHistList || []).filter(
            h => h.category === 'R',
        );

        for (let i = 0; i < residues.length; i++) {
            const r = residues[i];
            if (!r.workDt?.trim()) {
                toastWarning(
                    `유기질비료 ${i + 1}번째: 작업일자를 선택해주세요.`,
                );
                return false;
            }
            if (!r.rcod?.trim()) {
                toastWarning(
                    `유기질비료 ${i + 1}번째: 처리방법을 선택해주세요.`,
                );
                return false;
            }
            if (!r.ramt?.toString().trim()) {
                toastWarning(
                    `유기질비료 ${i + 1}번째: 처리량(kg/ha)을 입력해주세요.`,
                );
                return false;
            }
            if (!r.rdep?.toString().trim()) {
                toastWarning(
                    `유기질비료 ${i + 1}번째: 혼합깊이(cm)를 입력해주세요.`,
                );
                return false;
            }
        }

        // 병충해 관리
        const pestControls = (formData.farmingHistList || []).filter(
            h => h.category === 'C',
        );

        for (let i = 0; i < pestControls.length; i++) {
            const r = pestControls[i];
            if (!r.workDt?.trim()) {
                toastWarning(
                    `병해충관리 ${i + 1}번째: 작업일자를 선택해주세요.`,
                );
                return false;
            }
            if (!r.chcod?.trim()) {
                toastWarning(`병해충관리 ${i + 1}번째: 농약을 선택해주세요.`);
                return false;
            }
            if (!r.chme?.trim()) {
                toastWarning(
                    `병해충관리 ${i + 1}번째: 살포방법을 선택해주세요.`,
                );
                return false;
            }
            if (!r.chamt?.toString().trim()) {
                toastWarning(
                    `병해충관리 ${i + 1}번째: 처리량(kg/ha)을 입력해주세요.`,
                );
                return false;
            }
        }

        // 밭갈이(T) 데이터 검증 추가
        const tillages = (formData.farmingHistList || []).filter(
            h => h.category === 'T',
        );

        for (let i = 0; i < tillages.length; i++) {
            const r = tillages[i];
            if (!r.workDt?.trim()) {
                toastWarning(`밭갈이 ${i + 1}번째: 작업일자를 선택해주세요.`);
                return false;
            }
            if (!r.timpl?.trim()) {
                toastWarning(`밭갈이 ${i + 1}번째: 경운기구를 선택해주세요.`);
                return false;
            }
            if (!r.tdep?.toString().trim()) {
                toastWarning(
                    `밭갈이 ${i + 1}번째: 경운깊이(cm)를 입력해주세요.`,
                );
                return false;
            }
        }

        // 재배환경(E) 데이터 검증
        const environments = (formData.farmingHistList || []).filter(
            h => h.category === 'E',
        );

        for (let i = 0; i < environments.length; i++) {
            const r = environments[i];
            if (!r.workDt?.trim()) {
                toastWarning(`재배환경 ${i + 1}번째: 작업일자를 선택해주세요.`);
                return false;
            }
            if (!r.ename?.trim()) {
                toastWarning(`재배환경 ${i + 1}번째: 환경조건을 선택해주세요.`);
                return false;
            }
        }

        return true;
    };

    const handleSave = async () => {
        const isValid = await validateForm();
        if (!isValid) return;

        // 생육관리가 활성화된 경우 생육작물/품종코드/파종일시는 필수값
        if (formData.growthFlag === 1) {
            if (!formData.gcIdx) {
                toastWarning('생육작물을 선택해주세요.');
                return;
            }
            if (!formData.guIdx) {
                toastWarning('품종코드를 선택해주세요.');
                return;
            }
            if (!formData.pdate || formData.pdate.trim() === '') {
                toastWarning('파종일시를 선택해주세요.');
                return;
            }
        }

        // farmingHistList 검증 추가
        const isFarmingHistValid = validateFarmingHistList();
        if (!isFarmingHistValid) return;

        // 토양데이터 필터링 (입력된 데이터만)
        const validSoilTests = soilTests.filter(
            test => test.testDt && test.testDt.trim() !== '',
        );

        const submitData: FarmlandAddReq = {
            ...formData,
            soilTestList:
                validSoilTests.length > 0 ? validSoilTests : undefined,
        };

        await Alert.confirmAndExecute(
            '농지정보를 등록하시겠습니까?',
            '잘못된 정보가 없는지 확인해주세요.',
            true,
            async () => {
                await api.farmlandAdd(submitData).then(() => {
                    param.onSuccess();
                    closePop();
                });
            },
        );
    };

    const farmlandInfoContent = (
        <>
            <div className="row mb-3">
                <div className="col-md-2">
                    <label className="form-label mb-md-0 mt-2">
                        농지명<span className="text-danger">*</span> :
                    </label>
                </div>
                <div className="col-md-10">
                    <TextInput
                        value={formData.farmlandName}
                        onChange={value =>
                            handleInputChange('farmlandName', value)
                        }
                        placeholder="농지명을 입력해주세요."
                        required={true}
                        label="농지명"
                        minLength={3}
                        maxLength={30}
                    />
                </div>
            </div>

            <div className="row mb-3">
                <div className="col-md-2">
                    <label className="form-label mb-md-0 mt-2">
                        주소<span className="text-danger">*</span> :
                    </label>
                </div>
                <div className="col-md-10">
                    <TextInput
                        value={formData.address}
                        onChange={value => handleInputChange('address', value)}
                        placeholder="주소를 입력해주세요."
                        disabled={true}
                    />
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-md-2">
                    <label className="form-label mb-md-0 mt-2">
                        면적<span className="text-danger">*</span> :
                    </label>
                </div>
                <div className="col-md-10">
                    <TextInput
                        value={`${formData.area} ㎡`}
                        onChange={value => handleInputChange('area', value)}
                        placeholder="면적을 입력해주세요."
                        disabled={true}
                    />
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-md-2">
                    <label className="form-label mb-md-0 mt-2">
                        지목<span className="text-danger">*</span> :
                    </label>
                </div>
                <div className="col-md-10">
                    <TextInput
                        value={formData.jimok}
                        onChange={value => handleInputChange('jimok', value)}
                        placeholder="지목을 입력해주세요."
                        disabled={true}
                    />
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-md-2">
                    <label className="form-label mb-md-0 mt-2">
                        위도<span className="text-danger">*</span> :
                    </label>
                </div>
                <div className="col-md-10">
                    <TextInput
                        value={formData.lat}
                        onChange={value => handleInputChange('lat', value)}
                        placeholder="위도를 입력해주세요."
                        disabled={true}
                    />
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-md-2">
                    <label className="form-label mb-md-0 mt-2">
                        경도<span className="text-danger">*</span> :
                    </label>
                </div>
                <div className="col-md-10">
                    <TextInput
                        value={formData.lon}
                        onChange={value => handleInputChange('lon', value)}
                        placeholder="경도를 입력해주세요."
                        disabled={true}
                    />
                </div>
            </div>
        </>
    );

    const plantingInfoContent = (
        <GrowthManagement
            formData={formData}
            onInputChange={handleInputChange}
        />
    );

    const soilInfoContent = (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="text-muted">
                    <i className="ri-information-line me-1"></i>
                    토양분석 데이터는 선택사항입니다. 최대 5개까지 추가할 수
                    있습니다.
                </div>
                <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={addSoilTest}
                    disabled={soilTests.length >= 5}
                >
                    <i className="ri-add-line me-1"></i>토양데이터 추가
                </button>
            </div>

            {soilTests.length === 0 ? (
                <div className="text-center text-muted py-5">
                    <i className="ri-information-line fs-1 mb-3 d-block"></i>
                    <p>등록된 토양분석 데이터가 없습니다.</p>
                </div>
            ) : (
                soilTests.map((soil, index) => (
                    <div key={index} className="card mb-3">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">토양분석 #{index + 1}</h6>
                            {
                                <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={() => removeSoilTest(index)}
                                >
                                    <i className="ri-delete-bin-line"></i>
                                </button>
                            }
                        </div>
                        <div className="card-body">
                            <div className="row g-2">
                                <div className="col-md-8">
                                    <label className="form-label small">
                                        시험일자
                                    </label>
                                    <DatePicker
                                        selectedDate={parseDate(
                                            soil.testDt || '',
                                        )}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'testDt',
                                                formatDateString(value),
                                            )
                                        }
                                        maxDate={new Date()}
                                        showYearDropdown={false}
                                        showMonthDropdown={false}
                                        placeholder="시험일자 선택"
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        시료 깊이 (cm)
                                    </label>
                                    <TextInput
                                        value={soil.depth}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'depth',
                                                value,
                                            )
                                        }
                                        placeholder="깊이"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        토성
                                    </label>
                                    <SelectInput
                                        options={soilTextureOptions}
                                        value={
                                            soil.soilTexture?.toString() || ''
                                        }
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'soilTexture',
                                                value,
                                            )
                                        }
                                        placeholder="토성 선택"
                                        selectClassName="form-control"
                                        required={false}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        pH (-)
                                    </label>
                                    <TextInput
                                        value={soil.ph}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'ph',
                                                value,
                                            )
                                        }
                                        placeholder="pH"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        EC (dS/m)
                                    </label>
                                    <TextInput
                                        value={soil.ec}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'ec',
                                                value,
                                            )
                                        }
                                        placeholder="EC"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        유기물함량 (%)
                                    </label>
                                    <TextInput
                                        value={soil.organicMatter}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'organicMatter',
                                                value,
                                            )
                                        }
                                        placeholder="유기물함량"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        TOC (%)
                                    </label>
                                    <TextInput
                                        value={soil.toc}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'toc',
                                                value,
                                            )
                                        }
                                        placeholder="TOC"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        전질소 (%)
                                    </label>
                                    <TextInput
                                        value={soil.totalNitrogen}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'totalNitrogen',
                                                value,
                                            )
                                        }
                                        placeholder="전질소"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        암모니아 (mg/kg)
                                    </label>
                                    <TextInput
                                        value={soil.ammonia}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'ammonia',
                                                value,
                                            )
                                        }
                                        placeholder="암모니아"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        질산 (mg/kg)
                                    </label>
                                    <TextInput
                                        value={soil.nitricAcid}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'nitricAcid',
                                                value,
                                            )
                                        }
                                        placeholder="질산"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        CEC (cmol/kg)
                                    </label>
                                    <TextInput
                                        value={soil.cec}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'cec',
                                                value,
                                            )
                                        }
                                        placeholder="CEC"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        치환성 칼슘 (mg/kg)
                                    </label>
                                    <TextInput
                                        value={soil.exchangeableCa}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'exchangeableCa',
                                                value,
                                            )
                                        }
                                        placeholder="칼슘"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        치환성 마그네슘 (mg/kg)
                                    </label>
                                    <TextInput
                                        value={soil.exchangeableMg}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'exchangeableMg',
                                                value,
                                            )
                                        }
                                        placeholder="마그네슘"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        치환성 칼륨 (mg/kg)
                                    </label>
                                    <TextInput
                                        value={soil.exchangeableK}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'exchangeableK',
                                                value,
                                            )
                                        }
                                        placeholder="칼륨"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        유효인산 (mg/kg)
                                    </label>
                                    <TextInput
                                        value={soil.availablePhosphorus}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'availablePhosphorus',
                                                value,
                                            )
                                        }
                                        placeholder="유효인산"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        가비중 (g/cm³)
                                    </label>
                                    <TextInput
                                        value={soil.bulkDensity}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'bulkDensity',
                                                value,
                                            )
                                        }
                                        placeholder="가비중"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        고상 (%)
                                    </label>
                                    <TextInput
                                        value={soil.solid}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'solid',
                                                value,
                                            )
                                        }
                                        placeholder="고상"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        액상 (%)
                                    </label>
                                    <TextInput
                                        value={soil.liquid}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'liquid',
                                                value,
                                            )
                                        }
                                        placeholder="액상"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        기상 (%)
                                    </label>
                                    <TextInput
                                        value={soil.air}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'air',
                                                value,
                                            )
                                        }
                                        placeholder="기상"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        공극률 (%)
                                    </label>
                                    <TextInput
                                        value={soil.porosity}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'porosity',
                                                value,
                                            )
                                        }
                                        placeholder="공극률"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        수분함량 (%)
                                    </label>
                                    <TextInput
                                        value={soil.moistureMatter}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'moistureMatter',
                                                value,
                                            )
                                        }
                                        placeholder="수분함량"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>

                                <div className="col-md-2">
                                    <label className="form-label small">
                                        모래 (%)
                                    </label>
                                    <TextInput
                                        value={soil.sand}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'sand',
                                                value,
                                            )
                                        }
                                        placeholder="모래"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        미사 (%)
                                    </label>
                                    <TextInput
                                        value={soil.silt}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'silt',
                                                value,
                                            )
                                        }
                                        placeholder="미사"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small">
                                        점토 (%)
                                    </label>
                                    <TextInput
                                        value={soil.clay}
                                        onChange={value =>
                                            handleSoilTestChange(
                                                index,
                                                'clay',
                                                value,
                                            )
                                        }
                                        placeholder="점토"
                                        numericOnly={true}
                                        allowDecimal={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    const tabs = [
        {
            id: 'farmland-info',
            label: '농지정보',
            icon: 'ri-map-pin-line',
            content: farmlandInfoContent,
        },
        {
            id: 'planting-info',
            label: '생육관리',
            icon: 'ri-seedling-line',
            content: plantingInfoContent,
            isNoPadding: true,
        },
        {
            id: 'soil-info',
            label: '토양정보',
            icon: 'ri-test-tube-line',
            content: soilInfoContent,
        },
    ];

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            ...Object.fromEntries(
                Object.entries(param).filter(([key]) => key in formData),
            ),
        }));
        openPop();
    }, []);

    return (
        <>
            {isModalOpen && (
                <Modal
                    title="농지등록"
                    size="xl"
                    onClose={closePop}
                    hideFooter={true}
                >
                    <div className="card custom-card shadow-none mb-0">
                        <div className="card-body">
                            <Tabs
                                tabs={tabs}
                                defaultActiveTab="farmland-info"
                            />
                        </div>

                        <div className="card-footer">
                            <div className="btn-list text-end">
                                <ActionButton
                                    type="save"
                                    onClick={handleSave}
                                />
                                <ActionButton
                                    type="cancel"
                                    onClick={closePop}
                                />
                            </div>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default FarmlandAdd;
